import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Table, Button, Form, Modal, Spinner, Dropdown, Tabs, Tab } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaMoneyBillWave } from 'react-icons/fa';
import Swal from 'sweetalert2';
import CreatableSelect from 'react-select/creatable';
import { getAllExpensesApi, createExpenseApi, updateExpenseApi, deleteExpenseApi, getIssueByIdApi } from '../menu/service/api_service';
import ViewIssueModal from '../inventory/ViewIssueModal';
import { EXPENSE_CATEGORIES } from './expense_config';

export default function ExpenseMaster() {
  const token = localStorage.getItem('token');
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [selectedIssueDoc, setSelectedIssueDoc] = useState(null);
  
  const currentDate = new Date();
  const [filterMonth, setFilterMonth] = useState(currentDate.getMonth() + 1);
  const [filterYear, setFilterYear] = useState(currentDate.getFullYear());
  const [selectedFilterCats, setSelectedFilterCats] = useState([]);
  const [availableFilterCats, setAvailableFilterCats] = useState([]);
  const [activeTab, setActiveTab] = useState('general');

  const [formData, setFormData] = useState({
    category: null,
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAllExpensesApi(token, filterMonth, filterYear);
      setExpenses(res.data || []);
      
      // Combine predefined categories with any existing custom ones from DB
      const dbCats = [...new Set((res.data || []).map(e => e.category))];
      
      const uniqueCats = [...dbCats];
      EXPENSE_CATEGORIES.forEach(cat => {
        if (!uniqueCats.includes(cat)) uniqueCats.push(cat);
      });
      setCategories(uniqueCats.map(c => ({ value: c, label: c })));
      
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch expenses', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, filterMonth, filterYear]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  useEffect(() => {
    const currentTabExpenses = expenses.filter(e => activeTab === 'general' ? (!e.reference_type && e.category !== 'stock_loss') : (e.reference_type || e.category === 'stock_loss'));
    setAvailableFilterCats([...new Set(currentTabExpenses.map(e => e.category))]);
  }, [expenses, activeTab]);

  const handleShowAdd = () => {
    setFormData({ 
      category: null, 
      amount: '', 
      description: '', 
      date: new Date().toISOString().split('T')[0] 
    });
    setIsEditing(false);
    setShowModal(true);
  };

  const handleShowEdit = (expense) => {
    setSelectedExpense(expense);
    setFormData({
      category: { value: expense.category, label: expense.category },
      amount: expense.amount,
      description: expense.description || '',
      date: expense.created_at ? new Date(expense.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await deleteExpenseApi(token, id);
        Swal.fire('Deleted!', 'Expense has been deleted.', 'success');
        fetchExpenses();
      } catch (err) {
        console.error(err);
        Swal.fire('Error', 'Failed to delete expense', 'error');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category) {
      Swal.fire('Warning', 'Please select or enter a category', 'warning');
      return;
    }

    const payload = {
      category: formData.category.value,
      amount: formData.amount,
      description: formData.description,
      date: formData.date
    };

    try {
      if (isEditing) {
        await updateExpenseApi(token, selectedExpense.id, payload);
        Swal.fire('Success', 'Expense updated successfully', 'success');
      } else {
        await createExpenseApi(token, payload);
        Swal.fire('Success', 'Expense added successfully', 'success');
      }
      setShowModal(false);
      fetchExpenses();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to save expense', 'error');
    }
  };

  const handleCheckboxChange = (cat) => {
    setSelectedFilterCats(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const filteredExpenses = expenses.filter(e => 
    selectedFilterCats.length === 0 || selectedFilterCats.includes(e.category)
  );

  const handleSystemRowClick = async (expense) => {
    if (expense.reference_type === 'Issue' && expense.reference_id) {
      try {
        const res = await getIssueByIdApi(token, expense.reference_id);
        if (res.data) {
          setSelectedIssueDoc(res.data);
          setShowIssueModal(true);
        }
      } catch (error) {
        console.error("Failed to fetch issue details", error);
        Swal.fire('Error', 'ไม่สามารถดึงข้อมูลเอกสารเบิกได้', 'error');
      }
    } else if (expense.reference_type === 'Order') {
      Swal.fire('Info', `เอกสารออเดอร์ (Order #${expense.reference_id})`, 'info');
    }
  };

  const generalExpenses = filteredExpenses.filter(e => !e.reference_type && e.category !== 'stock_loss');
  const systemExpenses = filteredExpenses.filter(e => e.reference_type || e.category === 'stock_loss');

  return (
    <Container fluid className="p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100%" }}>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h2 className="fw-bold mb-0" style={{ color: "#1e3c72" }}><FaMoneyBillWave className="me-2" /> Expense Management</h2>
          <p className="text-muted mb-0 mt-1">Manage general and system expenses</p>
        </div>
        
        <div className="d-flex flex-wrap align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <span className="fw-bold text-muted small">Month:</span>
            <Form.Select 
              size="sm" 
              value={filterMonth} 
              onChange={e => setFilterMonth(e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="">All</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>{new Date(0, m - 1).toLocaleString('default', { month: 'long' })}</option>
              ))}
            </Form.Select>
          </div>
          
          <div className="d-flex align-items-center gap-2">
            <span className="fw-bold text-muted small">Year:</span>
            <Form.Select 
              size="sm" 
              value={filterYear} 
              onChange={e => setFilterYear(e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="">All</option>
              {[0, 1, 2, 3, 4].map(y => {
                const year = new Date().getFullYear() - y;
                return <option key={year} value={year}>{year}</option>
              })}
            </Form.Select>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="fw-bold text-muted small">Category:</span>
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" size="sm" id="dropdown-category">
                {selectedFilterCats.length === 0 ? "All Categories" : `${selectedFilterCats.length} Selected`}
              </Dropdown.Toggle>
              <Dropdown.Menu className="p-3 shadow-sm" style={{ minWidth: '250px' }}>
                <div className="fw-bold text-muted small mb-2">Filter by Category:</div>
                <div className="d-flex flex-column gap-2 max-h-50 overflow-auto" style={{ maxHeight: '300px' }}>
                  {availableFilterCats.map(cat => (
                    <Form.Check 
                      key={cat}
                      type="checkbox"
                      id={`filter-${cat}`}
                      label={cat}
                      checked={selectedFilterCats.includes(cat)}
                      onChange={() => handleCheckboxChange(cat)}
                    />
                  ))}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <Button className="px-4 shadow-sm ms-2 rounded text-white" style={{ backgroundColor: "#1e3c72", borderColor: "#1e3c72" }} onClick={handleShowAdd}>
            <FaPlus className="me-2" /> Add Expense
          </Button>
        </div>
      </div>



      <Tabs 
        activeKey={activeTab} 
        onSelect={(k) => { setActiveTab(k); setSelectedFilterCats([]); }} 
        id="expense-tabs" 
        className="mb-3 custom-tabs"
      >
        <Tab eventKey="general" title="General Expense">
          <Card className="shadow-sm border-0 bg-white" style={{ borderRadius: "15px" }}>
            <Card.Body className="p-0">
              <Table hover responsive className="align-middle mb-0">
                <thead style={{ backgroundColor: "#f1f4f8" }}>
                  <tr>
                    <th className="py-3 px-4 text-secondary font-weight-bold" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>No.</th>
                    <th className="py-3 text-secondary font-weight-bold" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>Date</th>
                    <th className="py-3 text-secondary font-weight-bold" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>Category</th>
                    <th className="py-3 text-secondary font-weight-bold" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>Description</th>
                    <th className="py-3 text-secondary font-weight-bold text-end" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>Amount (฿)</th>
                    <th className="py-3 text-secondary font-weight-bold" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>Created By</th>
                    <th className="py-3 px-4 text-secondary font-weight-bold text-center" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="7" className="text-center py-5"><Spinner animation="border" variant="primary" /></td></tr>
                  ) : generalExpenses.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-5 text-muted">No expenses found</td></tr>
                  ) : (
                    generalExpenses.map((expense, index) => (
                      <tr key={expense.id}>
                        <td className="px-4 text-muted">{index + 1}</td>
                        <td>{new Date(expense.created_at).toLocaleDateString()}</td>
                        <td><span className="badge bg-secondary">{expense.category}</span></td>
                        <td>{expense.description || '-'}</td>
                        <td className="text-end fw-bold text-danger">{Number(expense.amount).toLocaleString()}</td>
                        <td>{expense.created_by_name || '-'}</td>
                        <td className="px-4 text-center">
                          <Button variant="light" size="sm" className="me-2" onClick={() => handleShowEdit(expense)}>
                            <FaEdit className="text-primary" />
                          </Button>
                          <Button variant="light" size="sm" onClick={() => handleDelete(expense.id)}>
                            <FaTrash className="text-danger" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
        <Tab eventKey="system" title="System Expense">
          <Card className="shadow-sm border-0 bg-white" style={{ borderRadius: "15px" }}>
            <Card.Body className="p-0">
              <Table hover responsive className="align-middle mb-0">
                <thead style={{ backgroundColor: "#f1f4f8" }}>
                  <tr>
                    <th className="py-3 px-4 text-secondary font-weight-bold" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>No.</th>
                    <th className="py-3 text-secondary font-weight-bold" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>Date</th>
                    <th className="py-3 text-secondary font-weight-bold" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>Category</th>
                    <th className="py-3 text-secondary font-weight-bold" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>Description</th>
                    <th className="py-3 text-secondary font-weight-bold text-end" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>Amount (฿)</th>
                    <th className="py-3 text-secondary font-weight-bold" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>Created By</th>
                    <th className="py-3 px-4 text-secondary font-weight-bold text-center" style={{ backgroundColor: "#f1f4f8", borderBottom: "none" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="7" className="text-center py-5"><Spinner animation="border" variant="primary" /></td></tr>
                  ) : systemExpenses.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-5 text-muted">No expenses found</td></tr>
                  ) : (
                    systemExpenses.map((expense, index) => (
                      <tr 
                        key={expense.id}
                        style={{ cursor: expense.reference_type ? "pointer" : "default" }}
                        onClick={() => handleSystemRowClick(expense)}
                      >
                        <td className="px-4 text-muted">{index + 1}</td>
                        <td>{new Date(expense.created_at).toLocaleString()}</td>
                        <td><span className="badge bg-secondary">{expense.category}</span></td>
                        <td>{expense.description || '-'}</td>
                        <td className="text-end fw-bold text-danger">{Number(expense.amount).toLocaleString()}</td>
                        <td>{expense.created_by_name || '-'}</td>
                        <td className="px-4 text-center">
                          <span className="text-muted small">System generated</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="fw-bold">{isEditing ? 'Edit Expense' : 'Add Expense'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small text-muted">Date</Form.Label>
              <Form.Control 
                type="date"
                required
                value={formData.date}
                onChange={e => setFormData({...formData, date: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small text-muted">Category</Form.Label>
              <CreatableSelect
                isClearable
                options={categories}
                value={formData.category}
                onChange={(newValue) => setFormData({...formData, category: newValue})}
                placeholder="Select or type to create new category..."
                formatCreateLabel={(inputValue) => `Create category "${inputValue}"`}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small text-muted">Amount (฿)</Form.Label>
              <Form.Control 
                type="number" 
                step="0.01" 
                min="0"
                required
                value={formData.amount}
                onChange={e => setFormData({...formData, amount: e.target.value})}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small text-muted">Description (Optional)</Form.Label>
              <Form.Control 
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={e => setFormData({...formData, description: e.target.value})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="light" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ViewIssueModal 
        show={showIssueModal}
        onHide={() => setShowIssueModal(false)}
        doc={selectedIssueDoc}
      />
    </Container>
  );
}
